import { NavLink } from 'react-router-dom'
import { useUIStore } from '@/store/uiStore'
import { useAuthStore } from '@/store/authStore'
import { authService, PERMISSIONS } from '@/services/authService'
import {
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  BanknotesIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CogIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  ChartPieIcon,
  WalletIcon,
  CreditCardIcon,
  UserIcon,
} from '@heroicons/react/24/outline'

const navigation = [
  {
    name: '仪表板',
    href: '/dashboard',
    icon: HomeIcon,
    permission: null,
  },
  {
    name: '用户管理',
    href: '/users',
    icon: UsersIcon,
    permission: PERMISSIONS.USERS_VIEW,
    roles: ['admin', 'operator'], // 管理员和运营商都可以查看
  },
  {
    name: '后台用户',
    href: '/operators',
    icon: UserGroupIcon,
    permission: 'admin', // 只有 admin 角色可以看到
  },
  {
    name: '充值订单',
    href: '/deposits',
    icon: BanknotesIcon,
    permission: PERMISSIONS.WITHDRAWALS_VIEW,
  },
  {
    name: '交易管理',
    href: '/trades',
    icon: SparklesIcon,
    permission: PERMISSIONS.WITHDRAWALS_VIEW,
  },
  {
    name: '提现审核',
    href: '/withdrawals',
    icon: ArrowUpTrayIcon,
    permission: PERMISSIONS.WITHDRAWALS_VIEW,
  },
  {
    name: '钱包管理',
    href: '/wallets',
    icon: WalletIcon,
    permission: 'admin', // 只有管理员可以看到
  },
  {
    name: '我的钱包',
    href: '/my-wallet',
    icon: WalletIcon,
    permission: null,
    roles: ['operator'], // 只有运营商可以看到
  },
  {
    name: '我的充值',
    href: '/my-deposits',
    icon: BanknotesIcon,
    permission: null,
    roles: ['operator'], // 只有运营商可以看到
  },
  {
    name: '我的提现',
    href: '/my-withdrawals',
    icon: CreditCardIcon,
    permission: null,
    roles: ['operator'], // 只有运营商可以看到
  },
  {
    name: '行情管理',
    href: '/market-data',
    icon: ChartPieIcon,
    permission: null,
  },
  {
    name: '审计日志',
    href: '/audit',
    icon: DocumentTextIcon,
    permission: PERMISSIONS.AUDIT_VIEW,
  },
  {
    name: '数据报表',
    href: '/reports',
    icon: ChartBarIcon,
    permission: PERMISSIONS.REPORTS_VIEW,
  },
  {
    name: '参数配置',
    href: '/parameters',
    icon: AdjustmentsHorizontalIcon,
    permission: PERMISSIONS.SETTINGS_VIEW,
  },
  {
    name: '系统设置',
    href: '/settings',
    icon: CogIcon,
    permission: PERMISSIONS.SETTINGS_VIEW,
  },
] as const

type NavigationItem = {
  name: string
  href: string
  icon: typeof HomeIcon
  permission: string | null
  roles?: string[]
}

export default function Sidebar() {
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore()
  const { user } = useAuthStore()

  // Filter navigation items based on user permissions
  const filteredNavigation = navigation.filter(item => {
    // 如果没有权限要求，显示给所有人
    if (!item.permission) return true
    
    // 如果指定了角色列表，检查用户角色是否在列表中
    if (item.roles && Array.isArray(item.roles)) {
      return item.roles.includes(user?.role || '')
    }
    
    // 如果权限是角色名称（如 'admin'），检查用户角色
    if (item.permission === 'admin' || item.permission === 'finance' || item.permission === 'audit' || item.permission === 'operator') {
      return user?.role === item.permission
    }
    
    // 否则检查权限
    return authService.hasPermission(user, item.permission)
  })

  return (
    <div className="sidebar h-full flex flex-col">
      {/* Logo */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
        {!sidebarCollapsed && (
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">T</span>
            </div>
            <span className="text-lg font-semibold text-gray-900">交易平台</span>
          </div>
        )}
        
        <button
          onClick={toggleSidebarCollapsed}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          {sidebarCollapsed ? (
            <ChevronRightIcon className="w-5 h-5" />
          ) : (
            <ChevronLeftIcon className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav flex-1">
        {filteredNavigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            className={({ isActive }: { isActive: boolean }) =>
              isActive ? 'sidebar-nav-item-active' : 'sidebar-nav-item-inactive'
            }
            title={sidebarCollapsed ? item.name : undefined}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && (
              <span className="ml-3">{item.name}</span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* User info */}
      {!sidebarCollapsed && user && (
        <div className="px-4 py-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-600 font-medium text-sm">
                {user.full_name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.full_name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.role === 'admin' ? '系统管理员' : 
                 user.role === 'finance' ? '财务管理员' : 
                 user.role === 'audit' ? '审计管理员' : '运营商'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}